import { format, isValid } from "date-fns";
import type { ScheduleSnapshot } from "@/domains/pm/types";
import type { Language } from "@/lib/i18n/types";
import { formatDateOnly } from "@/domains/pm/utils/protocol";

/**
 * What a snapshot is called wherever it is named: its label, or — saved
 * without one — the local day and time it was taken. The time is what tells
 * two unnamed snapshots from the same day apart, on screen and in the
 * accessible names of their row buttons.
 */
export function snapshotName(
  snapshot: Pick<ScheduleSnapshot, "label" | "created_at">,
  language: Language
): string {
  if (snapshot.label) return snapshot.label;
  const taken = new Date(snapshot.created_at);
  // Rendered in the list, banner and compare dialog: a malformed timestamp
  // shows as itself rather than throwing out of render.
  if (!isValid(taken)) return snapshot.created_at;
  return `${snapshotDay(snapshot, language)} ${format(taken, "HH:mm")}`;
}

/**
 * The local day a snapshot was taken. Every date shown for a snapshot goes
 * through this: slicing `created_at` gives the UTC day, which near midnight
 * disagrees with the local time printed beside it.
 */
export function snapshotDay(
  snapshot: Pick<ScheduleSnapshot, "created_at">,
  language: Language
): string {
  const taken = new Date(snapshot.created_at);
  if (!isValid(taken)) return snapshot.created_at;
  return formatDateOnly(format(taken, "yyyy-MM-dd"), language);
}
